import { useMemo } from 'react';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useSelectedOrgId } from '@/hooks/common/useSelectedOrgId';
import { useDealOperations } from '@/hooks/deals/useDealOperations';
import type { ChatContextItem } from '@/types/chat.types';
import type { DealWithTitle } from 'src/types/deal.types';
import { DealStage } from '@equaliq/api-model-v2';

// "Untitled Deal" is what NewDealPage stamps on creation when the user
// doesn't fill a title; the enriched fallback is `Deal <8 chars>`. Both read
// as placeholders, so prefer the first attached filename when present.
const isPlaceholderTitle = (t: string | undefined): boolean => {
  if (!t) return true;
  const trimmed = t.trim();
  return trimmed.length === 0 || trimmed.toLowerCase() === 'untitled deal';
};

const stripFileExt = (name: string): string => name.replace(/\.[^./]+$/, '');

function dealDisplayName(deal: DealWithTitle): string {
  if (!isPlaceholderTitle(deal.title)) return deal.title;
  if (deal.firstFileName) return stripFileExt(deal.firstFileName);
  return deal.title || `Deal ${deal.dealId.slice(0, 8)}`;
}

const STAGE_LABELS: Record<string, string> = {
  [DealStage.drafting]: 'Drafting',
  [DealStage.negotiation]: 'Negotiation',
  [DealStage.signing]: 'Signing',
  [DealStage.delivery]: 'Delivery',
  [DealStage.completed]: 'Completed',
  [DealStage.cancelled]: 'Cancelled',
};

function dealDescription(deal: DealWithTitle): string {
  const stage = deal.latestStage ? STAGE_LABELS[deal.latestStage] ?? deal.latestStage : undefined;
  const docs =
    deal.documentsCount && deal.documentsCount > 0
      ? `${deal.documentsCount} document${deal.documentsCount === 1 ? '' : 's'}`
      : undefined;
  return [stage, docs].filter(Boolean).join(' · ');
}

const isCompleteAnalysis = (status: string | undefined): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'complete' || s === 'completed' || s === 'success';
};

/**
 * Real-mode counterpart to `useDemoDealsDocuments` for the Ari chat-context
 * picker. Projects the user's real deals (and their completed contract
 * analyses) into the same `ChatContextItem[]` shape the demo path produces,
 * so the "Add context" dropdown offers the deals/documents the user actually
 * has instead of the canned demo set.
 *
 * Honest empty: when the user has no real deals (e.g. a fresh org), the
 * returned list is naturally `[]` — the dropdown shows only the static page
 * items, which is the correct "no deals yet" result, not a fabricated one.
 */
export function useRealDealsDocuments(): { realDealItems: ChatContextItem[] } {
  const isDemo = useIsDemo();
  const selectedOrgId = useSelectedOrgId();

  // Demo mode never touches the real backend — the demo path owns its own
  // context items via useDemoDealsDocuments. Passing `undefined` nulls the
  // SWR key so no request fires and `deals` stays the stable empty array.
  const { deals } = useDealOperations(isDemo ? undefined : selectedOrgId);

  const realDealItems = useMemo<ChatContextItem[]>(() => {
    if (isDemo) return [];

    const dealItems: ChatContextItem[] = deals.map((deal) => ({
      id: `page-deal-${deal.dealId}`,
      type: 'page' as const,
      label: dealDisplayName(deal),
      value: `/deal/${deal.dealId}`,
      description: dealDescription(deal) || 'Deal',
    }));

    // A deal with a completed analysis is reachable at /analysis/:contractId.
    // Only completed runs get a chip — a pending/running/failed analysis has
    // nothing for Ari to read yet.
    const analysisItems: ChatContextItem[] = deals
      .filter((deal) => deal.primaryContractId && isCompleteAnalysis(deal.latestAnalysisStatus))
      .map((deal) => ({
        id: `page-analysis-${deal.primaryContractId}`,
        type: 'page' as const,
        label: dealDisplayName(deal),
        value: `/analysis/${deal.primaryContractId}`,
        description: 'Analysis',
      }));

    return [...dealItems, ...analysisItems];
  }, [deals, isDemo]);

  return { realDealItems };
}
